// eslint-disable-next-line import/prefer-default-export
import { resetState } from '@/store'
import http from '@/libs/axios'

export const auth = {
  namespaced: true,
  state: {
    isRefreshing: false,
    refreshingCall: false,
    auth: {
      user: {},
      token: {
        access_token: '',
        refresh_token: '',
      },
      company: {
      },
      role: '',
      permissions: [],
      bank: {},
    },
    banks: [],
    countries: [],
    regions: [],
    cities: [],
    currency: process.env.VUE_APP_CURRENCY,
    userActivities: {},
    daysLeft: 0,
    hasLoanAccess: false,
    sms_balance: {
      id: null,
      balance: 0,
      is_active: false,
    },
    current_module: null,
    pricing: [],
    subscription: {},
    modules: [],
    remember_me: false,
    plans: [],
    multipleAccounts: null,
  },
  mutations: {
    setMultipleAccountsState(state, data) {
      state.multipleAccounts = data
    },
    setModules(state, modules) {
      state.modules = modules
    },
    setSubscriptionDaysLeft(state, days) {
      state.daysLeft = days
    },
    setHasLoanAccess(state, access) {
      state.hasLoanAccess = access
    },
    setSubAndPricing(state, data) {
      state.plans = data
    },
    setCurrentModule(state, module) {
      state.current_module = module
    },
    setSmsBalance(state, balance) {
      state.sms_balance = balance
    },
    removePhoto(state) {
      state.auth.user.photo = null
    },
    setUserActivities(state, activities) {
      state.userActivities = activities
    },
    setAddresses(state, data) {
      state.countries = data.countries
      state.regions = data.regions
      state.cities = data.cities
    },
    setBanksAddresses(state, data) {
      state.banks = data.banks
      state.countries = data.countries
      state.regions = data.regions
      state.cities = data.cities
      state.auth.company = { ...state.auth.company, ...data.company }
      state.auth.bank = data.bank
    },
    updateBank(state, bank) {
      state.auth.bank = bank
    },
    updateLogo(state, logo) {
      state.auth.company.logo = logo
    },
    updateAddress(state, address) {
      state.auth.address = address
    },
    updateToken(state, token) {
      state.auth.token = token
    },
    updateAccessToken(state, token) {
      state.auth.token.access_token = token
    },
    setRefreshingState(state, data) {
      state.isRefreshing = data
    },
    setRefreshingCall(state, data) {
      state.refreshingCall = data
    },
    setUserData: (state, userData) => {
      state.auth = { ...state.auth, ...userData }
    },
    setUserPermissions: (state, permissions) => {
      state.auth.permissions = permissions
      localStorage.setItem('permissions', JSON.stringify(permissions))
    },
    setRememberMe: (state, r) => {
      state.remember_me = r
    },
    // eslint-disable-next-line no-unused-vars
    clearAuthState(state) {
      resetState()
    },
    setLoggedOut(state) {
      state.auth.token.access_token = null
    },
    updateUser(state, user) {
      state.auth.user = { ...state.auth.user, ...user }
    },
    updateUserPhoto(state, photo) {
      state.auth.user.photo = photo
    },
    updateCompany(state, company) {
      state.auth.company = { ...state.auth.company, ...company }
    },
  },
  getters: {
    multipleAccounts(state) {
      return state.multipleAccounts
    },
    totalStudents(state) {
      return state.totalStudents
    },
    plans(state) {
      return state.plans
    },
    role(state) {
      return state.auth.role
    },
    permissions(state) {
      return state.auth.permissions
    },
    formattedPermissions(state) {
      let { permissions } = state.auth
      permissions = permissions?.map(p => ({
        subject: p.resource,
        action: p.name,
      }))
      if (state.auth.role === 'Administrator') {
        permissions = null
      }
      if (permissions !== null) {
        // eslint-disable-next-line no-unused-expressions
        permissions?.push({
          subject: 'sikanii',
          action: 'access-sikanii-app',
        })
      }
      return permissions
    },
    isCustom(state) {
      return state.auth.current_academic_year.custom
    },
    accessToken(state) {
      return state.auth?.token?.access_token
    },
    refreshToken(state) {
      return state.auth?.token?.refresh_token
    },
    appBarData(state) {
      return {
        first_name: state.auth?.user?.first_name,
        role: state.auth?.role,
        company_name: state.auth?.company.name,
        sms_sender_name: state.auth?.company.sms_sender_name,
        short_title: '',
        title: '',
      }
    },
    academicYear(state) {
      return state.auth?.current_academic_year
    },
    user(state) {
      return state.auth.user
    },
    verified(state) {
      return true
    },
    userProfile(state) {
      return state.auth.user?.photo ? `${state.auth.user?.photo}` : null
    },
    companyName(state) {
      return state.auth.company.name
    },
    companyCode(state) {
      return state.auth.company.verification_code
    },
    companyLogo(state) {
      return state.auth.company?.logo ? state.auth.company?.logo : null
    },
    company(state) {
      return state.auth.company
    },
    bank(state) {
      return state.auth.bank
    },
    banks(state) {
      return state.banks
    },
    countries(state) {
      return state.countries
    },
    regions(state) {
      return state.regions
    },
    cities(state) {
      return state.cities
    },
    address(state) {
      return state.auth.address
    },
    currency(state) {
      return state.currency
    },
    userActivities(state) {
      return state.userActivities
    },
    smsBalance(state) {
      return state.sms_balance?.balance
    },
    hasActivatedSmsAccount(state) {
      return !!state.sms_balance?.id
    },
    smsActivated(state) {
      return state.sms_balance?.is_active
    },
    currentModule(state) {
      return state.current_module
    },
    trialEndDate(state) {
      return state.auth.company.trial_ends
    },
    pricing(state) {
      return state.pricing
    },
    subscription(state) {
      return state.subscription
    },
    companyModules(state) {
      return state.modules
    },
    rememberMe(state) {
      return state.remember_me
    },
    hasMultipleTerms(state) {
      return state.auth.has_multiple_terms
    },
    hasMultipleAccounts(state) {
      return state.auth.has_multiple_accounts
    },
    daysLeft(state) {
      return state.daysLeft
    },
    hasLoanAccess(state) {
      return state.hasLoanAccess
    },
  },
}
